<script setup>
import {
  useCalendar,
  useDayjs,
} from '@daysmart/daysmart-vue-components/composables'
import { useCalendarSettings } from '../composables/useCalendarSettings'

const props = defineProps({
  events: Array,
  date: String,
  closedDates: {
    type: Array,
    default: () => [],
  },
})

const { t } = useI18n()
const dayjs = useDayjs()

const { customFirstDayOfWeek } = useCalendarSettings()
const calendar = useCalendar({ customFirstDayOfWeek })

const week = computed(() => calendar.getWeekView.value(props.date))

function getIsClosed(day) {
  return props.closedDates.map(d => dayjs.value(d).dayOfYear()).includes(Number(day))
}

function getDate(day) {
  return week.value.map(d => d.format('YYYY-MM-DD')).find(d => dayjs.value(d).dayOfYear() === day)
}

const hasNoEvents = computed(() => props.events.length === 0)
</script>

<template>
  <div class="week-view">
    <div class="desktop-calendar-venues-view lt-sm:hidden">
      <DesktopScrollContainer>
        <template v-if="hasNoEvents">
          <ScheduleNoEvents
            class="my-4"
            :text="t('There are no events scheduled for this week')"
          />
          <div
            v-if="closedDates.length"
            class="flex flex-col bg-surface-600 last:(border-b-1 border-t-1 border-surface-600 rounded-8px)"
          >
            <DSGroupedDataTemplate
              v-slot="{
                key: groupKey,
                isFirst: isFirstCol,
                isLast: isLastCol,
              } = {}"
              class="w-full flex flex-row flex-gap-1px px-1px"
              :get-group-key="event => dayjs(event.starts_at.slice(0, 19)).dayOfYear()"
              :initial-group-keys="week.map(d => d.dayOfYear())"
            >
              <ScheduleEventItemContainer
                :group-key="groupKey"
                :class="{
                  'rounded-bl-8px': isLastRow && isFirstCol,
                  'rounded-br-8px': isLastRow && isLastCol,
                }"
              >
                <ScheduleClosedSign v-if="getIsClosed(groupKey)" />
              </ScheduleEventItemContainer>
            </DSGroupedDataTemplate>
          </div>
        </template>
        <ScheduleGroupedViewTemplate
          v-else
          :data="events"
          :get-group-key="
            event => dayjs(event.starts_at.slice(0, 19)).dayOfYear()
          "
          :initial-group-keys="week.map(d => d.dayOfYear())"
          :date="getDate"
          :is-closed="(day) => getIsClosed(day)"
        />
      </DesktopScrollContainer>
    </div>
  </div>
</template>

<i18n>
{
  "en-us": {
    "There are no events scheduled for this day": "There are no events scheduled for this day",
    "There are no events scheduled for this week": "There are no events scheduled for this week"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "There are no events scheduled for this day": "Für diesen Tag sind keine Veranstaltungen geplant",
  "There are no events scheduled for this week": "Für diese Woche sind keine Veranstaltungen geplant"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "There are no events scheduled for this day": "Il n'y a aucun événement programmé pour ce jour",
  "There are no events scheduled for this week": "Il n'y a aucun événement programmé pour cette semaine"
}
</i18n>

<i18n locale="es" lang="json">
{
  "There are no events scheduled for this day": "No hay eventos programados para este día",
  "There are no events scheduled for this week": "No hay eventos programados para esta semana"
}
</i18n>
